import { useState, useEffect } from "react";
import client from "../lib/api";
import { toast } from "sonner";

export default function AccountSettingsPage() {
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [saving, setSaving] = useState(false);
  const [me, setMe] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get("/auth/me");
        setMe(data);
      } catch {
        /* ignore */
      }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (newPw !== confirmPw) {
      toast.error("Passwords don't match.");
      return;
    }
    if (newPw === oldPw) {
      toast.error("New password must differ from the old one.");
      return;
    }
    setSaving(true);
    try {
      await client.post("/auth/change-password", {
        old_password: oldPw,
        new_password: newPw,
      });
      toast.success("Password changed. All other sessions signed out.");
      setOldPw("");
      setNewPw("");
      setConfirmPw("");
      // Refresh /me so the must_change_password banner disappears
      const { data } = await client.get("/auth/me");
      setMe(data);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Password change failed";
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto py-8 px-4 space-y-6" data-testid="account-settings-page">
      <header>
        <h1 className="text-2xl font-semibold text-white">Account settings</h1>
        <p className="text-sm text-neutral-500 mt-1">
          Signed in as <span className="text-white">{me?.email || "…"}</span>
          {me?.is_admin && (
            <span className="ml-2 inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest bg-[#FF5A1F]/15 text-[#FF9764] border border-[#FF5A1F]/30">
              Admin
            </span>
          )}
        </p>
      </header>

      {me?.must_change_password && (
        <div
          className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-amber-200 text-sm"
          data-testid="must-change-password-banner"
        >
          <b>Change your password.</b> You're using the temporary password
          from your invitation email — please rotate it now.
        </div>
      )}

      <div className="card-surface p-6 rounded-lg border border-white/10">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-3">
          Change password
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs text-neutral-400 block mb-1">Current password</label>
            <input
              type="password"
              value={oldPw}
              onChange={(e) => setOldPw(e.target.value)}
              required
              data-testid="account-old-password"
              className="w-full bg-white/[0.03] border border-white/10 rounded-md text-white text-sm py-2 px-3 focus:outline-none focus:border-[#FF5A1F]/60"
            />
          </div>
          <div>
            <label className="text-xs text-neutral-400 block mb-1">New password</label>
            <input
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              required
              minLength={8}
              data-testid="account-new-password"
              className="w-full bg-white/[0.03] border border-white/10 rounded-md text-white text-sm py-2 px-3 focus:outline-none focus:border-[#FF5A1F]/60"
            />
            <div className="text-[11px] text-neutral-600 mt-1">Minimum 8 characters.</div>
          </div>
          <div>
            <label className="text-xs text-neutral-400 block mb-1">Confirm new password</label>
            <input
              type="password"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              required
              data-testid="account-confirm-password"
              className="w-full bg-white/[0.03] border border-white/10 rounded-md text-white text-sm py-2 px-3 focus:outline-none focus:border-[#FF5A1F]/60"
            />
          </div>
          <button
            type="submit"
            disabled={saving || !oldPw || !newPw || !confirmPw}
            data-testid="account-change-password-btn"
            className="rounded-full px-5 py-2 text-sm font-semibold bg-[#FF5A1F] hover:bg-[#FF7038] text-black disabled:opacity-50"
          >
            {saving ? "Changing…" : "Change password"}
          </button>
        </form>
        <div className="text-[11px] text-neutral-500 mt-4">
          Changing your password signs you out of all other devices immediately.
        </div>
      </div>

      <SmsNotificationsCard />
    </div>
  );
}

const TZ_LABELS = {
  "America/New_York": "Eastern",
  "America/Chicago": "Central",
  "America/Denver": "Mountain",
  "America/Phoenix": "Arizona",
  "America/Los_Angeles": "Pacific",
  "America/Anchorage": "Alaska",
  "Pacific/Honolulu": "Hawaii",
};

function SmsNotificationsCard() {
  const [phone, setPhone] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [tz, setTz] = useState("America/Chicago");
  const [tzs, setTzs] = useState(Object.keys(TZ_LABELS));
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sharePhone, setSharePhone] = useState("");
  const [sharing, setSharing] = useState(false);
  const [optouts, setOptouts] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get("/account/sms-settings");
        setPhone(data.phone || "");
        setEnabled(!!data.enabled);
        setTz(data.timezone || "America/Chicago");
        if (data.timezones?.length) setTzs(data.timezones);
      } catch {
        /* ignore */
      }
      try {
        const { data } = await client.get("/account/sms-optouts");
        setOptouts(data.optouts || []);
      } catch {
        /* ignore */
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await client.post("/account/sms-settings", {
        phone,
        enabled,
        timezone: tz,
      });
      setPhone(data.phone);
      if (data.welcome_sms?.sent) {
        toast.success("Saved — welcome text sent to your phone 📱");
      } else if (data.welcome_sms?.queued) {
        toast.success("Saved — it's quiet hours, your welcome text sends after 7am.");
      } else {
        toast.success("Text notification settings saved.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await client.post("/account/sms-settings/test");
      if (data.sent) toast.success("Test text sent — check your phone.");
      else if (data.queued) toast.success("Quiet hours right now — test text queued for after 7am.");
      else toast.error(data.reason || "Test text failed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test text failed");
    } finally {
      setTesting(false);
    }
  };

  const sendShare = async () => {
    setSharing(true);
    try {
      const { data } = await client.post("/account/sms-settings/share", { phone: sharePhone });
      if (data.sent) toast.success("Setup link texted — tell them to check their phone.");
      else if (data.queued) toast.success("Quiet hours — the setup link sends after 7am their time.");
      else if (data.reason === "opted_out") toast.error("That number replied STOP — they need to text START first.");
      else toast.error(data.reason || "Couldn't send");
      if (data.sent || data.queued) setSharePhone("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send");
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="card-surface p-6 rounded-lg border border-white/10" data-testid="sms-settings-card">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
        Text notifications
      </div>
      <p className="text-xs text-neutral-400 mb-4">
        Load alerts, hot-streak congrats and dispatch texts — straight to your phone.
        <span className="text-neutral-500"> Texts only send between 7am–9pm in your timezone.</span>
      </p>
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-neutral-400 block mb-1">Mobile number</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(605) 555-0123"
              data-testid="sms-phone-input"
              className="w-full bg-white/[0.03] border border-white/10 rounded-md text-white text-sm py-2 px-3 focus:outline-none focus:border-[#FF5A1F]/60"
            />
          </div>
          <div>
            <label className="text-xs text-neutral-400 block mb-1">Your timezone</label>
            <select
              value={tz}
              onChange={(e) => setTz(e.target.value)}
              data-testid="sms-timezone-select"
              className="w-full bg-white/[0.03] border border-white/10 rounded-md text-white text-sm py-2 px-3 focus:outline-none focus:border-[#FF5A1F]/60"
            >
              {tzs.map((z) => (
                <option key={z} value={z} className="bg-[#111]">
                  {TZ_LABELS[z] || z}
                </option>
              ))}
            </select>
          </div>
        </div>
        <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="sms-enabled-toggle">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="h-4 w-4 accent-[#FF5A1F]"
            data-testid="sms-enabled-checkbox"
          />
          <span className="text-sm text-neutral-300">
            Text me notifications{" "}
            <span className="text-neutral-500 text-xs">(reply STOP anytime to opt out)</span>
          </span>
        </label>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={save}
            disabled={saving || !phone.trim()}
            data-testid="sms-save-btn"
            className="rounded-full px-5 py-2 text-sm font-semibold bg-[#FF5A1F] hover:bg-[#FF7038] text-black disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save & send welcome text"}
          </button>
          <button
            onClick={sendTest}
            disabled={testing || !enabled || !phone.trim()}
            data-testid="sms-test-btn"
            className="rounded-full px-5 py-2 text-sm border border-white/15 text-neutral-300 hover:border-white/40 hover:bg-white/5 disabled:opacity-40"
          >
            {testing ? "Sending…" : "Send test text"}
          </button>
        </div>

        {optouts.length > 0 && (
          <div
            className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3"
            data-testid="sms-optouts-list"
          >
            <div className="text-xs font-semibold text-amber-200 mb-1.5">
              ⚠ {optouts.length} number{optouts.length === 1 ? "" : "s"} replied STOP — texts to them are paused
            </div>
            <ul className="space-y-1">
              {optouts.map((o, i) => (
                <li key={i} className="text-xs text-amber-100/80 flex items-center gap-2" data-testid={`sms-optout-row-${i}`}>
                  <span className="font-mono">{o.phone}</span>
                  <span className="text-amber-200/60">· {o.label}</span>
                </li>
              ))}
            </ul>
            <div className="text-[11px] text-amber-200/60 mt-1.5">
              They can text <b>START</b> to our number anytime to turn texts back on.
            </div>
          </div>
        )}

        <div className="pt-4 border-t border-white/[0.06]">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
            Text this setup link to a driver
          </div>
          <p className="text-xs text-neutral-500 mb-2">
            They get a text with a link to this page so they can turn on their own notifications. Max 15/day.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="tel"
              value={sharePhone}
              onChange={(e) => setSharePhone(e.target.value)}
              placeholder="Driver's mobile number"
              data-testid="sms-share-phone-input"
              className="flex-1 min-w-[180px] bg-white/[0.03] border border-white/10 rounded-md text-white text-sm py-2 px-3 focus:outline-none focus:border-[#FF5A1F]/60"
            />
            <button
              onClick={sendShare}
              disabled={sharing || !sharePhone.trim()}
              data-testid="sms-share-btn"
              className="rounded-full px-5 py-2 text-sm font-semibold border border-[#FF5A1F]/50 text-[#FF9764] hover:bg-[#FF5A1F]/10 disabled:opacity-40"
            >
              {sharing ? "Sending…" : "Text setup link"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
